import { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdmin } from '../../utils/roles';

const AnalyticsChartsGrid = lazy(() => import('./components/AnalyticsChartsGrid'));

interface ChartData {
  name: string;
  value?: number;
  amount?: number;
  contributions?: number;
  loans?: number;
  [key: string]: string | number | undefined;
}

type TimeRange = '7d' | '30d' | '90d' | '1y';

function startDateForRange(range: TimeRange): Date {
  const d = new Date();
  if (range === '7d') d.setDate(d.getDate() - 7);
  if (range === '30d') d.setDate(d.getDate() - 30);
  if (range === '90d') d.setDate(d.getDate() - 90);
  if (range === '1y') d.setFullYear(d.getFullYear() - 1);
  return d;
}

function formatDayLabel(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatMonthLabel(date: Date): string {
  return date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
}

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export default function AdvancedAnalytics() {
  const { user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [selectedMetric, setSelectedMetric] = useState<string>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Live chart data
  const [contributionTrend, setContributionTrend] = useState<ChartData[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<ChartData[]>([]);
  const [memberActivity, setMemberActivity] = useState<ChartData[]>([]);
  const [groupComparison, setGroupComparison] = useState<ChartData[]>([]);

  // KPI values
  const [kpis, setKpis] = useState({
    totalValue: 0,
    activeMembers: 0,
    avgContribution: 0,
    loanUtilizationPct: 0,
  });

  const [detailedStats, setDetailedStats] = useState({
    contributionsThisMonth: 0,
    contributionsLastMonth: 0,
    loansThisMonth: 0,
    loansLastMonth: 0,
    newMembersThisMonth: 0,
    newMembersLastMonth: 0,
  });

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  const metrics = [
    { id: 'overview', label: 'Overview', icon: 'BarChart' },
    { id: 'contributions', label: 'Contributions', icon: 'TrendingUp' },
    { id: 'loans', label: 'Loans', icon: 'CreditCard' },
    { id: 'members', label: 'Members', icon: 'Users' },
    { id: 'performance', label: 'Performance', icon: 'Award' },
  ];

  const timeRanges = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
  ];

  const kpiCards = useMemo(() => {
    const fmtMoney = (n: number) => `VT ${Math.round(n).toLocaleString()}`;
    return [
      {
        title: 'Total Contributions',
        value: fmtMoney(kpis.totalValue),
        change: '',
        trend: 'up',
        icon: 'DollarSign',
        color: 'blue',
      },
      {
        title: 'Active Members',
        value: kpis.activeMembers.toLocaleString(),
        change: '',
        trend: 'up',
        icon: 'Users',
        color: 'green',
      },
      {
        title: 'Avg. Contribution',
        value: fmtMoney(kpis.avgContribution),
        change: '',
        trend: 'up',
        icon: 'TrendingUp',
        color: 'purple',
      },
      {
        title: 'Loan Utilization',
        value: `${Math.round(kpis.loanUtilizationPct)}%`,
        change: '',
        trend: kpis.loanUtilizationPct >= 0 ? 'up' : 'down',
        icon: 'Percent',
        color: 'orange',
      },
    ];
  }, [kpis]);

  // Load groups accessible to current user (super_admin: all groups, group_admin: own groups)
  useEffect(() => {
    const loadGroups = async () => {
      if (!user || !userProfile) return;
      setError(null);
      setLoading(true);

      try {
        const superAdmin = isSuperAdmin(userProfile.role);

        if (superAdmin) {
          const { data, error: e } = await supabase
            .from('groups')
            .select('id, name')
            .eq('is_active', true)
            .order('created_at', { ascending: false });
          if (e) throw e;
          const list = (data || []).map((g: any) => ({ id: g.id, name: g.name || 'Unnamed Group' }));
          setGroups(list);
          setSelectedGroupId((prev) => (prev && list.some(x => x.id === prev) ? prev : list[0]?.id || null));
          return;
        }

        // group_admin (and admins): groups they created OR where they are admin/clerk member
        const [{ data: createdGroups, error: createdErr }, { data: memberships, error: memberErr }] = await Promise.all([
          supabase
            .from('groups')
            .select('id, name')
            .eq('created_by', user.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false }),
          supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', user.id)
            .in('role', ['admin', 'clerk']),
        ]);

        if (createdErr) throw createdErr;
        if (memberErr) throw memberErr;

        const groupIds = Array.from(
          new Set([
            ...(createdGroups || []).map((g: any) => g.id),
            ...(memberships || []).map((m: any) => m.group_id).filter(Boolean),
          ]),
        );

        let list: Array<{ id: string; name: string }> = (createdGroups || []).map((g: any) => ({
          id: g.id,
          name: g.name || 'Unnamed Group',
        }));

        const missingIds = groupIds.filter((id) => !list.some((g) => g.id === id));
        if (missingIds.length) {
          const { data: extraGroups, error: extraErr } = await supabase
            .from('groups')
            .select('id, name')
            .in('id', missingIds)
            .eq('is_active', true);
          if (extraErr) throw extraErr;
          list = list.concat((extraGroups || []).map((g: any) => ({ id: g.id, name: g.name || 'Unnamed Group' })));
        }

        // Dedupe just in case
        const seen = new Set<string>();
        list = list.filter((g) => (seen.has(g.id) ? false : (seen.add(g.id), true)));
        setGroups(list);
        setSelectedGroupId((prev) => (prev && list.some(x => x.id === prev) ? prev : list.length === 1 ? list[0].id : list[0]?.id || null));
      } catch (e: any) {
        console.error('Failed to load groups for analytics:', e);
        setError(e?.message || 'Failed to load groups');
      } finally {
        setLoading(false);
      }
    };

    loadGroups();
  }, [user, userProfile]);

  // Load analytics for selected group + time range
  useEffect(() => {
    const loadAnalytics = async () => {
      if (!user || !userProfile || !selectedGroupId) return;
      setError(null);
      setLoading(true);

      try {
        const startDate = startDateForRange(timeRange);
        const startIso = startDate.toISOString();
        const now = new Date();
        const thisMonthStart = startOfMonth(now);
        const lastMonthStart = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));

        // Contributions for group and range
        const { data: contribs, error: contribErr } = await supabase
          .from('contributions')
          .select('amount, contribution_date, status')
          .eq('group_id', selectedGroupId)
          .gte('contribution_date', startIso)
          .order('contribution_date', { ascending: true });
        if (contribErr) throw contribErr;

        const totalAmount = (contribs || []).reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);
        const totalCount = (contribs || []).length;

        // Member count
        const { count: memberCount, error: memberCountErr } = await supabase
          .from('group_members')
          .select('*', { count: 'exact', head: true })
          .eq('group_id', selectedGroupId);
        if (memberCountErr) throw memberCountErr;

        // Loans (fallback: loan_requests)
        let loans: any[] = [];
        try {
          const { data: loanReqs, error: loanReqErr } = await supabase
            .from('loan_requests')
            .select('amount, created_at, status')
            .eq('group_id', selectedGroupId)
            .gte('created_at', startIso)
            .order('created_at', { ascending: true });
          if (loanReqErr) throw loanReqErr;
          loans = loanReqs || [];
        } catch (e) {
          console.warn('Loan requests query failed:', e);
          loans = [];
        }

        const approvedLoanAmount = loans
          .filter((l: any) => String(l.status || '').toLowerCase() === 'approved')
          .reduce((sum: number, l: any) => sum + Number(l.amount || 0), 0);

        const utilization = totalAmount > 0 ? (approvedLoanAmount / totalAmount) * 100 : 0;

        setKpis({
          totalValue: totalAmount,
          activeMembers: memberCount || 0,
          avgContribution: totalCount > 0 ? totalAmount / totalCount : 0,
          loanUtilizationPct: utilization,
        });

        // Detailed stats table (this month vs last month)
        const [{ data: thisMonthContribs, error: tmcErr }, { data: lastMonthContribs, error: lmcErr }] =
          await Promise.all([
            supabase
              .from('contributions')
              .select('amount, contribution_date')
              .eq('group_id', selectedGroupId)
              .gte('contribution_date', thisMonthStart.toISOString())
              .lte('contribution_date', now.toISOString()),
            supabase
              .from('contributions')
              .select('amount, contribution_date')
              .eq('group_id', selectedGroupId)
              .gte('contribution_date', lastMonthStart.toISOString())
              .lt('contribution_date', thisMonthStart.toISOString()),
          ]);
        if (tmcErr) throw tmcErr;
        if (lmcErr) throw lmcErr;

        const thisMonthContributionAmount =
          (thisMonthContribs || []).reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0) || 0;
        const lastMonthContributionAmount =
          (lastMonthContribs || []).reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0) || 0;

        const [{ data: thisMonthLoans }, { data: lastMonthLoans }] = await Promise.all([
          supabase
            .from('loan_requests')
            .select('id, created_at, status')
            .eq('group_id', selectedGroupId)
            .gte('created_at', thisMonthStart.toISOString())
            .lte('created_at', now.toISOString()),
          supabase
            .from('loan_requests')
            .select('id, created_at, status')
            .eq('group_id', selectedGroupId)
            .gte('created_at', lastMonthStart.toISOString())
            .lt('created_at', thisMonthStart.toISOString()),
        ]);

        const approvedThisMonthLoans = (thisMonthLoans || []).filter(
          (l: any) => String(l.status || '').toLowerCase() === 'approved',
        ).length;
        const approvedLastMonthLoans = (lastMonthLoans || []).filter(
          (l: any) => String(l.status || '').toLowerCase() === 'approved',
        ).length;

        const [{ count: newThisMonthMembers }, { count: newLastMonthMembers }] = await Promise.all([
          supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', selectedGroupId)
            .gte('joined_at', thisMonthStart.toISOString())
            .lte('joined_at', now.toISOString()),
          supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', selectedGroupId)
            .gte('joined_at', lastMonthStart.toISOString())
            .lt('joined_at', thisMonthStart.toISOString()),
        ]);

        setDetailedStats({
          contributionsThisMonth: thisMonthContributionAmount,
          contributionsLastMonth: lastMonthContributionAmount,
          loansThisMonth: approvedThisMonthLoans,
          loansLastMonth: approvedLastMonthLoans,
          newMembersThisMonth: newThisMonthMembers || 0,
          newMembersLastMonth: newLastMonthMembers || 0,
        });

        // Trend chart: daily for <=90d, monthly for 1y
        if (timeRange === '1y') {
          const byMonth = new Map<string, number>();
          (contribs || []).forEach((c: any) => {
            const d = new Date(c.contribution_date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            byMonth.set(key, (byMonth.get(key) || 0) + Number(c.amount || 0));
          });
          const trend = Array.from(byMonth.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, amount]) => {
              const [y, m] = key.split('-').map(Number);
              return { name: formatMonthLabel(new Date(y, m - 1, 1)), amount, value: amount };
            });
          setContributionTrend(trend);
        } else {
          const start = startOfDay(startDate);
          const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
          const map = new Map<string, { amount: number; count: number }>();
          for (let i = 0; i < days; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            map.set(formatDayLabel(d), { amount: 0, count: 0 });
          }
          (contribs || []).forEach((c: any) => {
            const key = formatDayLabel(new Date(c.contribution_date));
            const cur = map.get(key);
            if (cur) {
              cur.amount += Number(c.amount || 0);
              cur.count += 1;
            }
          });
          setContributionTrend(
            Array.from(map.entries()).map(([name, v]) => ({
              name,
              amount: v.amount,
              contributions: v.count,
              value: v.amount,
            })),
          );
        }

        // Category distribution: by contribution status (approved/pending/etc) within range
        const statusMap = new Map<string, number>();
        (contribs || []).forEach((c: any) => {
          const s = (c.status || 'unknown').toString();
          statusMap.set(s, (statusMap.get(s) || 0) + Number(c.amount || 0));
        });
        const dist = Array.from(statusMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([name, value]) => ({ name, value }));
        setCategoryDistribution(dist.length ? dist : [{ name: 'No data', value: 1 }]);

        // Member activity: weekly counts in range (contrib count vs loan approved count)
        const weeks = 4;
        const weekStarts: Date[] = [];
        for (let i = weeks - 1; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i * 7);
          weekStarts.push(startOfDay(d));
        }
        const activity: ChartData[] = weekStarts.map((d, idx) => ({
          name: `Week ${idx + 1}`,
          contributions: 0,
          loans: 0,
          value: 0,
        }));

        (contribs || []).forEach((c: any) => {
          const dt = new Date(c.contribution_date);
          for (let i = 0; i < weekStarts.length; i++) {
            const start = weekStarts[i];
            const end = new Date(start);
            end.setDate(end.getDate() + 7);
            if (dt >= start && dt < end) {
              activity[i].contributions = Number(activity[i].contributions || 0) + 1;
              activity[i].value = Number(activity[i].contributions || 0);
              break;
            }
          }
        });

        (loans || []).forEach((l: any) => {
          if (String(l.status || '').toLowerCase() !== 'approved') return;
          const dt = new Date(l.created_at);
          for (let i = 0; i < weekStarts.length; i++) {
            const start = weekStarts[i];
            const end = new Date(start);
            end.setDate(end.getDate() + 7);
            if (dt >= start && dt < end) {
              activity[i].loans = Number(activity[i].loans || 0) + 1;
              break;
            }
          }
        });
        setMemberActivity(activity);

        // Group comparison: total contributions per group for this admin (range)
        if (groups.length > 1) {
          const groupIds = groups.map(g => g.id);
          const { data: contribAll, error: contribAllErr } = await supabase
            .from('contributions')
            .select('group_id, amount, contribution_date')
            .in('group_id', groupIds)
            .gte('contribution_date', startIso);
          if (contribAllErr) throw contribAllErr;

          const byGroup = new Map<string, number>();
          (contribAll || []).forEach((c: any) => {
            byGroup.set(c.group_id, (byGroup.get(c.group_id) || 0) + Number(c.amount || 0));
          });
          setGroupComparison(
            groups
              .map((g) => ({ name: g.name, value: byGroup.get(g.id) || 0 }))
              .sort((a, b) => Number(b.value || 0) - Number(a.value || 0)),
          );
        } else {
          setGroupComparison([{ name: groups[0]?.name || 'Group', value: totalAmount }]);
        }
      } catch (e: any) {
        console.error('Failed to load analytics:', e);
        setError(e?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [user, userProfile, selectedGroupId, timeRange, groups]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 relative">
      {/* Vanuatu Sand Drawing Pattern Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-[0.03] z-0">
        <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="sand-pattern-analytics" x="0" y="0" width="180" height="180" patternUnits="userSpaceOnUse">
              {/* Geometric pattern - data visualization inspired */}
              <path d="M20,20 L60,20 L90,60 L60,100 L20,100 Z" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.4" />
              {/* Spiral ascending - growth symbol */}
              <path d="M140,40 Q150,50 150,65 Q150,80 140,90 Q130,100 115,100 Q100,100 95,85 Q90,70 100,60 Q110,50 120,50"
                stroke="currentColor" strokeWidth="2" fill="none" opacity="0.35" />
              {/* Bar chart inspired pattern */}
              <rect x="120" y="120" width="8" height="40" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3" />
              <rect x="135" y="130" width="8" height="30" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3" />
              <rect x="150" y="125" width="8" height="35" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3" />
              {/* Connecting dots - data points */}
              <circle cx="30" cy="140" r="3" fill="currentColor" opacity="0.3" />
              <circle cx="50" cy="130" r="3" fill="currentColor" opacity="0.3" />
              <circle cx="70" cy="145" r="3" fill="currentColor" opacity="0.3" />
              <path d="M30,140 L50,130 L70,145" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.25" />
              {/* Traditional weave pattern */}
              <path d="M160,10 Q165,15 160,20 Q155,25 160,30" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#sand-pattern-analytics)" className="text-indigo-900" />
        </svg>
      </div>

      {/* Decorative sand pattern strip at top */}
      <div className="fixed top-20 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 opacity-20 z-10"></div>

      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex relative z-10">
        {/* Desktop sidebar */}
        <div className="hidden lg:block">
          <Sidebar isCollapsed={isSidebarCollapsed} />
        </div>

        {/* Mobile sidebar overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl">
              <Sidebar />
            </div>
          </div>
        )}

        <main
          className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
            }`}
        >
          <div className="p-4 lg:p-6">
            <h1 className="text-3xl font-bold mb-6 text-gray-900">Advanced Analytics</h1>

            {/* Hero Section */}
            <div className="mb-6 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Analytics Dashboard</h2>
                  <p className="text-blue-100">Comprehensive insights into your community's financial health</p>
                </div>
                {groups.length > 1 && (
                  <div className="min-w-[260px]">
                    <div className="text-xs text-blue-100 mb-1">Selected Group</div>
                    <select
                      className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/40"
                      value={selectedGroupId || ''}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      disabled={loading}
                    >
                      {groups.map((g) => (
                        <option key={g.id} value={g.id} className="text-gray-900">
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Controls */}
            <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                {metrics.map((metric) => (
                  <Button
                    key={metric.id}
                    variant={selectedMetric === metric.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedMetric(metric.id)}
                  >
                    📊 {metric.label}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                {timeRanges.map((range) => (
                  <Button
                    key={range.value}
                    variant={timeRange === range.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeRange(range.value as any)}
                  >
                    {range.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {kpiCards.map((kpi, index) => (
                <div key={index} className="relative overflow-hidden bg-white rounded-lg shadow p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{kpi.title}</p>
                      <p className="text-2xl font-bold text-gray-900 mb-2">{kpi.value}</p>
                      {kpi.change ? (
                        <div
                          className={`flex items-center text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                            }`}
                        >
                          <Icon
                            name={kpi.trend === 'up' ? 'TrendingUp' : 'TrendingDown'}
                            className="w-4 h-4 mr-1"
                          />
                          {kpi.change}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500">
                          {loading ? 'Updating…' : 'Live'}
                        </div>
                      )}
                    </div>
                    <div
                      className={`p-3 rounded-lg ${kpi.color === 'blue'
                          ? 'bg-blue-100'
                          : kpi.color === 'green'
                            ? 'bg-green-100'
                            : kpi.color === 'purple'
                              ? 'bg-purple-100'
                              : 'bg-orange-100'
                        }`}
                    >
                      <Icon
                        name={kpi.icon}
                        className={`w-6 h-6 ${kpi.color === 'blue'
                            ? 'text-blue-600'
                            : kpi.color === 'green'
                              ? 'text-green-600'
                              : kpi.color === 'purple'
                                ? 'text-purple-600'
                                : 'text-orange-600'
                          }`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Grid - filtered by selected metric */}
            <Suspense
              fallback={
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white rounded-lg shadow p-6 h-[380px]" />
                  <div className="bg-white rounded-lg shadow p-6 h-[380px]" />
                  <div className="bg-white rounded-lg shadow p-6 h-[380px]" />
                  <div className="bg-white rounded-lg shadow p-6 h-[380px]" />
                </div>
              }
            >
              <AnalyticsChartsGrid
                selectedMetric={selectedMetric}
                loading={loading}
                contributionTrend={contributionTrend}
                categoryDistribution={categoryDistribution}
                memberActivity={memberActivity}
                groupComparison={groupComparison}
                colors={COLORS}
              />
            </Suspense>

            {/* Detailed Stats Table */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  📋 Detailed Statistics
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Metric</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">This Month</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Last Month</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Change</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const fmtMoney = (n: number) => `VT ${Math.round(n).toLocaleString()}`;
                      const pctChange = (cur: number, prev: number) => {
                        if (prev === 0) return cur === 0 ? 0 : 100;
                        return ((cur - prev) / prev) * 100;
                      };
                      const rows = [
                        {
                          label: 'Total Contributions',
                          cur: detailedStats.contributionsThisMonth,
                          prev: detailedStats.contributionsLastMonth,
                          fmt: fmtMoney,
                        },
                        {
                          label: 'Approved Loans',
                          cur: detailedStats.loansThisMonth,
                          prev: detailedStats.loansLastMonth,
                          fmt: (n: number) => n.toLocaleString(),
                        },
                        {
                          label: 'New Members',
                          cur: detailedStats.newMembersThisMonth,
                          prev: detailedStats.newMembersLastMonth,
                          fmt: (n: number) => n.toLocaleString(),
                        },
                      ];

                      return rows.map((r) => {
                        const change = pctChange(r.cur, r.prev);
                        const up = change >= 0;
                        const changeLabel = `${up ? '+' : ''}${change.toFixed(1)}%`;
                        return (
                          <tr key={r.label} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{r.label}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">{r.fmt(r.cur)}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-600">{r.fmt(r.prev)}</td>
                            <td
                              className={`px-4 py-3 text-sm text-right font-medium ${up ? 'text-green-600' : 'text-red-600'
                                }`}
                            >
                              {changeLabel}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Icon
                                name={up ? 'TrendingUp' : 'TrendingDown'}
                                className={`w-4 h-4 inline ${up ? 'text-green-600' : 'text-red-600'}`}
                              />
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Export Options */}
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline">
                <Icon name="Download" className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline">
                <Icon name="FileText" className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
              <Button variant="default">
                <Icon name="Share" className="w-4 h-4 mr-2" />
                Share Report
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
  